import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { StatusBadge } from "@/components/StatusBadge";
import { formatCurrency, formatDate } from "@/lib/format";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export default function SuperInvoices() {
    const { t } = useTranslation();
    const [items, setItems] = useState([]);
    const [hotels, setHotels] = useState([]);
    const [status, setStatus] = useState("");

    const load = async () => {
        const [i, h] = await Promise.all([
            api.get(`/invoices${status ? `?status=${status}` : ""}`),
            api.get("/hotels"),
        ]);
        setItems(i.data); setHotels(h.data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

    const hotelName = (id) => hotels.find((h) => h.id === id)?.name || "—";

    const markPaid = async (inv) => {
        try { await api.patch(`/invoices/${inv.id}`, { status: "paid" }); toast.success("Marked paid"); await load(); }
        catch { toast.error("Failed"); }
    };
    const markFailed = async (inv) => {
        try { await api.patch(`/invoices/${inv.id}`, { status: "failed" }); toast.success("Marked failed"); await load(); }
        catch { toast.error("Failed"); }
    };

    const totalPaid = items.filter((i) => i.status === "paid").reduce((a, b) => a + b.amount, 0);
    const totalPending = items.filter((i) => i.status === "pending").reduce((a, b) => a + b.amount, 0);
    const totalFailed = items.filter((i) => i.status === "failed").reduce((a, b) => a + b.amount, 0);

    return (
        <>
            <Topbar title={t("super_admin.invoices_title")} subtitle={`${items.length} ${t("common.total")}`} />
            <div className="flex-1 overflow-auto p-6">
                <div className="mb-4 grid grid-cols-3 gap-3">
                    <SummaryCard label="Paid" value={formatCurrency(totalPaid)} count={items.filter((i) => i.status === "paid").length} tone="green" />
                    <SummaryCard label="Pending" value={formatCurrency(totalPending)} count={items.filter((i) => i.status === "pending").length} tone="amber" />
                    <SummaryCard label="Failed" value={formatCurrency(totalFailed)} count={items.filter((i) => i.status === "failed").length} tone="red" />
                </div>

                <div className="mb-3 flex gap-1">
                    {["", "paid", "pending", "failed"].map((s) => (
                        <button
                            key={s || "all"}
                            onClick={() => setStatus(s)}
                            className={`rounded-md border px-2.5 py-1 text-[12px] font-medium capitalize ${status === s ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:bg-muted/60"}`}
                            data-testid={`invoices-filter-${s || "all"}`}
                        >{s || "All"}</button>
                    ))}
                </div>

                <div className="akm-card overflow-hidden">
                    <table className="akm-table w-full">
                        <thead><tr><th>Invoice #</th><th>Hotel</th><th>Period</th><th>Amount</th><th>Due</th><th>Status</th><th>Paid at</th><th></th></tr></thead>
                        <tbody>
                            {items.map((i) => (
                                <tr key={i.id}>
                                    <td className="font-mono text-[11px]">{i.invoice_number}</td>
                                    <td className="font-medium">{hotelName(i.hotel_id)}</td>
                                    <td className="text-muted-foreground">{formatDate(i.period_start)} → {formatDate(i.period_end)}</td>
                                    <td className="font-mono">{formatCurrency(i.amount, i.currency)}</td>
                                    <td className="text-muted-foreground">{formatDate(i.due_date)}</td>
                                    <td><StatusBadge status={i.status === "paid" ? "active" : i.status === "failed" ? "error" : "pending"} label={i.status} /></td>
                                    <td className="text-muted-foreground">{i.paid_at ? formatDate(i.paid_at) : "—"}</td>
                                    <td className="text-right">
                                        {i.status !== "paid" && (
                                            <button onClick={() => markPaid(i)} className="mr-1 rounded-md border border-border bg-card px-2 py-0.5 text-[11px] text-green-700 hover:bg-green-50"><Check size={10} className="inline" /> Paid</button>
                                        )}
                                        {i.status === "pending" && (
                                            <button onClick={() => markFailed(i)} className="rounded-md border border-border bg-card px-2 py-0.5 text-[11px] text-red-700 hover:bg-red-50"><X size={10} className="inline" /> Fail</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && <tr><td colSpan="8" className="py-12 text-center text-muted-foreground">No invoices</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}

const TONE = { green: "text-green-700", amber: "text-amber-700", red: "text-red-700" };
function SummaryCard({ label, value, count, tone }) {
    return (
        <div className="akm-card p-4">
            <div className="akm-section-title">{label}</div>
            <div className={`mt-1 text-xl font-semibold tracking-tight ${TONE[tone]}`}>{value}</div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">{count} invoice{count === 1 ? "" : "s"}</div>
        </div>
    );
}
